import { LIVE_API } from './config'
import { db, commit } from './portalDb'
import { requireSession } from './portalAuthApi'
import { can, type PortalAction } from './portalPermissions'
import { http, latency, newId, paginate, PortalApiError, queryString, type PageQuery } from './portalHttp'
import type {
  AuditEntry,
  ConversationSummary,
  Deliverable,
  Invoice,
  InvoiceStatus,
  MemberWithUser,
  Message,
  Organisation,
  OrgRole,
  Paged,
  Payment,
  PortalNotification,
  PortalUser,
  Project,
  ProjectDetail,
  ProjectStatus,
  Task,
  TaskStatus,
} from '../types/portal'

/**
 * The portal API, one function per endpoint in the specification, under
 * /api/v1. In live mode each function is a single HTTP call. In the preview
 * the same function runs against the in-memory store and applies the same two
 * layers the backend does:
 *
 * 1. The RolesGuard: the caller's role in this organisation, from the session's
 *    orgMemberships, must permit the action, or FORBIDDEN.
 * 2. Row level security: rows from an organisation the caller does not belong
 *    to, or a project a member or viewer is not assigned to, are NOT_FOUND,
 *    so the response never confirms that they exist.
 */

// ─── Guards ───────────────────────────────────────────────────────────────

function scope(orgId: string, action?: PortalAction) {
  const session = requireSession()
  const membership = session.orgMemberships.find((m) => m.orgId === orgId)
  if (!membership) throw new PortalApiError('NOT_FOUND')
  if (action && !can(membership.role, action)) throw new PortalApiError('FORBIDDEN')
  return { userId: session.user.id, role: membership.role }
}

function visibleProjects(orgId: string, userId: string, role: OrgRole): Project[] {
  const inOrg = db.projects.filter((p) => p.orgId === orgId)
  if (can(role, 'projects.viewAll')) return inOrg
  const assigned = new Set(db.projectMembers.filter((m) => m.userId === userId).map((m) => m.projectId))
  return inOrg.filter((p) => assigned.has(p.id))
}

function projectScope(orgId: string, projectId: string, action?: PortalAction) {
  const ctx = scope(orgId, action)
  const project = visibleProjects(orgId, ctx.userId, ctx.role).find((p) => p.id === projectId)
  if (!project) throw new PortalApiError('NOT_FOUND')
  return { ...ctx, project }
}

function audit(orgId: string | null, userId: string, action: string, entityType: string, entityId: string | null, metadata: Record<string, string> | null = null) {
  db.auditLog.unshift({ id: newId('a'), orgId, userId, action, entityType, entityId, metadata, createdAt: new Date().toISOString() })
}

function notify(userIds: string[], input: Omit<PortalNotification, 'id' | 'userId' | 'isRead' | 'createdAt'>) {
  const now = new Date().toISOString()
  new Set(userIds).forEach((userId) => {
    db.notifications.unshift({ ...input, id: newId('n'), userId, isRead: false, createdAt: now })
  })
}

function userById(id: string): PortalUser {
  const user = db.users.find((u) => u.id === id)
  if (!user) throw new PortalApiError('NOT_FOUND')
  return { ...user }
}

// ─── Organisations and members ────────────────────────────────────────────

export async function getOrganisation(orgId: string): Promise<Organisation> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}`)
  await latency(120)
  scope(orgId)
  const org = db.organisations.find((o) => o.id === orgId)
  if (!org) throw new PortalApiError('NOT_FOUND')
  return { ...org }
}

export async function listMembers(orgId: string): Promise<MemberWithUser[]> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/members`)
  await latency()
  scope(orgId, 'members.list')
  const order: OrgRole[] = ['owner', 'admin', 'member', 'viewer']
  return db.orgMembers
    .filter((m) => m.orgId === orgId)
    .map((m) => ({ ...m, user: userById(m.userId) }))
    .sort((a, b) => order.indexOf(a.role) - order.indexOf(b.role))
}

export async function inviteMember(orgId: string, input: { email: string; role: OrgRole }): Promise<MemberWithUser> {
  const email = input.email.trim().toLowerCase()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new PortalApiError('VALIDATION_ERROR', 'Please enter a valid email address.', { email: 'Please enter a valid email address.' })
  }
  if (LIVE_API) {
    const result = await http<MemberWithUser>('POST', `/orgs/${orgId}/members/invite`, { email, role: input.role })
    commit()
    return result
  }
  await latency(400)
  const ctx = scope(orgId, 'members.invite')
  // Only an owner may grant owner.
  if (input.role === 'owner' && ctx.role !== 'owner') throw new PortalApiError('FORBIDDEN', 'Only an owner can invite another owner.')

  let user = db.users.find((u) => u.email === email)
  if (user && db.orgMembers.some((m) => m.orgId === orgId && m.userId === user?.id)) {
    throw new PortalApiError('VALIDATION_ERROR', 'This person is already a member of the organisation.', { email: 'Already a member.' })
  }
  if (!user) {
    user = { id: newId('u'), email, firstName: null, lastName: null, jobTitle: null, isInternal: false, internalRole: null, lastLoginAt: null }
    db.users.push(user)
  }
  const member = { id: newId('om'), orgId, userId: user.id, role: input.role, invitedBy: ctx.userId, joinedAt: new Date().toISOString() }
  db.orgMembers.push(member)
  audit(orgId, ctx.userId, 'member.invited', 'user', user.id, { email, role: input.role })
  commit()
  return { ...member, user: { ...user } }
}

export async function updateMemberRole(orgId: string, userId: string, role: OrgRole): Promise<void> {
  if (LIVE_API) {
    await http('PATCH', `/orgs/${orgId}/members/${userId}`, { role })
    commit()
    return
  }
  await latency()
  const ctx = scope(orgId, 'members.changeRole')
  const member = db.orgMembers.find((m) => m.orgId === orgId && m.userId === userId)
  if (!member) throw new PortalApiError('NOT_FOUND')
  const owners = db.orgMembers.filter((m) => m.orgId === orgId && m.role === 'owner')
  if (member.role === 'owner' && role !== 'owner' && owners.length === 1) {
    throw new PortalApiError('VALIDATION_ERROR', 'Every organisation needs at least one owner. Make someone else an owner first.')
  }
  const from = member.role
  member.role = role
  audit(orgId, ctx.userId, 'member.role_changed', 'user', userId, { from, to: role })
  commit()
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  if (LIVE_API) {
    await http('DELETE', `/orgs/${orgId}/members/${userId}`)
    commit()
    return
  }
  await latency()
  const ctx = scope(orgId, 'members.remove')
  const member = db.orgMembers.find((m) => m.orgId === orgId && m.userId === userId)
  if (!member) throw new PortalApiError('NOT_FOUND')
  if (userId === ctx.userId) throw new PortalApiError('VALIDATION_ERROR', 'You cannot remove yourself. Ask another admin or owner.')
  if (member.role === 'owner' && ctx.role !== 'owner') throw new PortalApiError('FORBIDDEN', 'Only an owner can remove an owner.')
  db.orgMembers = db.orgMembers.filter((m) => m !== member)
  const orgProjects = new Set(db.projects.filter((p) => p.orgId === orgId).map((p) => p.id))
  db.projectMembers = db.projectMembers.filter((pm) => !(pm.userId === userId && orgProjects.has(pm.projectId)))
  audit(orgId, ctx.userId, 'member.removed', 'user', userId, { email: userById(userId).email })
  commit()
}

// ─── Projects ─────────────────────────────────────────────────────────────

export interface ProjectQuery extends PageQuery {
  status?: ProjectStatus
  productId?: string
}

export async function listProjects(orgId: string, query: ProjectQuery = {}): Promise<Paged<Project>> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/projects${queryString({ ...query })}`)
  await latency()
  const ctx = scope(orgId, 'projects.viewAssigned')
  const rows = visibleProjects(orgId, ctx.userId, ctx.role)
    .filter((p) => (!query.status || p.status === query.status) && (!query.productId || p.productId === query.productId))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  return paginate(rows, query)
}

export async function getProject(orgId: string, projectId: string): Promise<ProjectDetail> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/projects/${projectId}`)
  await latency()
  const { project } = projectScope(orgId, projectId)
  const taskSummary: Record<TaskStatus, number> = { todo: 0, in_progress: 0, review: 0, done: 0 }
  db.tasks.filter((t) => t.projectId === projectId).forEach((t) => (taskSummary[t.status] += 1))
  return {
    project: { ...project },
    members: db.projectMembers.filter((m) => m.projectId === projectId).map((m) => ({ ...m, user: userById(m.userId) })),
    taskSummary,
  }
}

export async function updateProject(orgId: string, projectId: string, patch: Partial<Pick<Project, 'name' | 'status' | 'startDate' | 'endDate'>>): Promise<void> {
  if (LIVE_API) {
    await http('PATCH', `/orgs/${orgId}/projects/${projectId}`, patch)
    commit()
    return
  }
  await latency()
  const { project, userId } = projectScope(orgId, projectId, 'projects.update')
  Object.assign(project, patch, { updatedAt: new Date().toISOString() })
  audit(orgId, userId, 'project.updated', 'project', projectId, Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, String(v)])))
  commit()
}

// ─── Tasks ────────────────────────────────────────────────────────────────

export async function listTasks(orgId: string, projectId: string): Promise<Task[]> {
  if (LIVE_API) return (await http<Paged<Task>>('GET', `/orgs/${orgId}/projects/${projectId}/tasks?limit=100`)).data
  await latency()
  projectScope(orgId, projectId, 'projects.viewAssigned')
  return db.tasks.filter((t) => t.projectId === projectId).sort((a, b) => a.sortOrder - b.sortOrder).map((t) => ({ ...t }))
}

export async function createTask(orgId: string, projectId: string, input: { title: string; assignedTo: string | null; dueDate: string | null }): Promise<Task> {
  if (!input.title.trim()) throw new PortalApiError('VALIDATION_ERROR', 'Please give the task a title.', { title: 'Required.' })
  if (LIVE_API) {
    const task = await http<Task>('POST', `/orgs/${orgId}/projects/${projectId}/tasks`, input)
    commit()
    return task
  }
  await latency()
  const { userId, project } = projectScope(orgId, projectId, 'tasks.create')
  const sortOrder = Math.max(0, ...db.tasks.filter((t) => t.projectId === projectId).map((t) => t.sortOrder)) + 1
  const task: Task = { id: newId('t'), projectId, title: input.title.trim(), description: null, status: 'todo', assignedTo: input.assignedTo, dueDate: input.dueDate, sortOrder, createdBy: userId }
  db.tasks.push(task)
  if (input.assignedTo && input.assignedTo !== userId) {
    notify([input.assignedTo], { type: 'task.assigned', title: task.title, body: project.name, link: `/portal/orgs/${orgId}/projects/${projectId}?tab=tasks` })
  }
  commit()
  return { ...task }
}

export async function updateTask(orgId: string, projectId: string, taskId: string, patch: Partial<Pick<Task, 'status' | 'assignedTo' | 'dueDate'>>): Promise<void> {
  if (LIVE_API) {
    await http('PATCH', `/orgs/${orgId}/projects/${projectId}/tasks/${taskId}`, patch)
    commit()
    return
  }
  await latency(140)
  projectScope(orgId, projectId, 'tasks.update')
  const task = db.tasks.find((t) => t.id === taskId && t.projectId === projectId)
  if (!task) throw new PortalApiError('NOT_FOUND')
  Object.assign(task, patch)
  commit()
}

export async function deleteTask(orgId: string, projectId: string, taskId: string): Promise<void> {
  if (LIVE_API) {
    await http('DELETE', `/orgs/${orgId}/projects/${projectId}/tasks/${taskId}`)
    commit()
    return
  }
  await latency()
  const { userId } = projectScope(orgId, projectId, 'tasks.delete')
  const task = db.tasks.find((t) => t.id === taskId && t.projectId === projectId)
  if (!task) throw new PortalApiError('NOT_FOUND')
  db.tasks = db.tasks.filter((t) => t !== task)
  audit(orgId, userId, 'task.deleted', 'task', taskId, { title: task.title })
  commit()
}

// ─── Deliverables ─────────────────────────────────────────────────────────

/** The latest version of each deliverable, with its earlier versions attached. */
export interface DeliverableGroup {
  latest: Deliverable
  history: Deliverable[]
  project: Project
}

function groupDeliverables(rows: Deliverable[], projects: Project[]): DeliverableGroup[] {
  const byRoot = new Map<string, Deliverable[]>()
  rows.forEach((d) => {
    const root = d.parentId ?? d.id
    byRoot.set(root, [...(byRoot.get(root) ?? []), d])
  })
  return Array.from(byRoot.values())
    .map((versions) => {
      const sorted = [...versions].sort((a, b) => b.version - a.version)
      return { latest: sorted[0], history: sorted.slice(1), project: projects.find((p) => p.id === sorted[0].projectId) as Project }
    })
    .sort((a, b) => b.latest.createdAt.localeCompare(a.latest.createdAt))
}

export async function listDeliverables(orgId: string, projectId: string): Promise<DeliverableGroup[]> {
  if (LIVE_API) {
    const [rows, detail] = await Promise.all([
      http<Paged<Deliverable>>('GET', `/orgs/${orgId}/projects/${projectId}/deliverables?limit=100`),
      getProject(orgId, projectId),
    ])
    return groupDeliverables(rows.data, [detail.project])
  }
  await latency()
  const { project } = projectScope(orgId, projectId, 'deliverables.download')
  return groupDeliverables(db.deliverables.filter((d) => d.projectId === projectId && !d.deletedAt), [project])
}

/** Every deliverable the caller can see in the organisation, across their projects. */
export async function listOrgDeliverables(orgId: string): Promise<DeliverableGroup[]> {
  const projects = (await listProjects(orgId, { limit: 100 })).data
  const groups = await Promise.all(projects.map((p) => listDeliverables(orgId, p.id)))
  return groups.flat().sort((a, b) => b.latest.createdAt.localeCompare(a.latest.createdAt))
}

export async function uploadDeliverable(
  orgId: string,
  projectId: string,
  input: { file: File; name: string; description: string; parentId: string | null },
  onProgress?: (pct: number) => void,
): Promise<Deliverable> {
  if (!input.name.trim()) throw new PortalApiError('VALIDATION_ERROR', 'Please name the deliverable.', { name: 'Required.' })
  if (input.file.size > 100 * 1024 * 1024) throw new PortalApiError('VALIDATION_ERROR', 'Files can be up to 100 MB.', { file: 'Too large.' })

  if (LIVE_API) {
    const { uploadUrl, key } = await http<{ uploadUrl: string; key: string }>('POST', `/orgs/${orgId}/projects/${projectId}/deliverables/presign`, {
      filename: input.file.name,
      contentType: input.file.type || 'application/octet-stream',
      sizeBytes: input.file.size,
    })
    await putWithProgress(uploadUrl, input.file, onProgress)
    const record = await http<Deliverable>('POST', `/orgs/${orgId}/projects/${projectId}/deliverables`, {
      s3Key: key,
      name: input.name,
      description: input.description || null,
      filename: input.file.name,
      mimeType: input.file.type || 'application/octet-stream',
      sizeBytes: input.file.size,
      parentId: input.parentId,
    })
    commit()
    return record
  }

  const { userId, project } = projectScope(orgId, projectId, 'deliverables.upload')
  for (let pct = 0; pct <= 100; pct += 20) {
    onProgress?.(pct)
    await latency(140)
  }
  const root = input.parentId ? db.deliverables.find((d) => d.id === input.parentId && d.projectId === projectId) : null
  if (input.parentId && !root) throw new PortalApiError('NOT_FOUND')
  const version = root ? Math.max(...db.deliverables.filter((d) => (d.parentId ?? d.id) === root.id).map((d) => d.version)) + 1 : 1
  const record: Deliverable = {
    id: newId('d'),
    projectId,
    name: input.name.trim(),
    description: input.description.trim() || null,
    filename: input.file.name,
    mimeType: input.file.type || 'application/octet-stream',
    sizeBytes: input.file.size,
    version,
    parentId: root?.id ?? null,
    uploadedBy: userId,
    createdAt: new Date().toISOString(),
    deletedAt: null,
  }
  db.deliverables.push(record)
  project.updatedAt = record.createdAt
  audit(orgId, userId, 'deliverable.uploaded', 'deliverable', record.id, { name: record.name, version: String(version) })
  notify(
    db.projectMembers.filter((m) => m.projectId === projectId && m.userId !== userId).map((m) => m.userId),
    { type: 'deliverable.uploaded', title: `${record.name}${version > 1 ? `, version ${version}` : ''}`, body: `Uploaded to ${project.name}.`, link: `/portal/orgs/${orgId}/projects/${projectId}?tab=deliverables` },
  )
  commit()
  return { ...record }
}

/** Returns a short lived download URL. The raw storage path is never exposed. */
export async function downloadDeliverable(orgId: string, projectId: string, deliverableId: string): Promise<{ url: string; expiresAt: string }> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/projects/${projectId}/deliverables/${deliverableId}/download`)
  await latency(300)
  const { userId } = projectScope(orgId, projectId, 'deliverables.download')
  const file = db.deliverables.find((d) => d.id === deliverableId && d.projectId === projectId && !d.deletedAt)
  if (!file) throw new PortalApiError('NOT_FOUND')
  audit(orgId, userId, 'deliverable.downloaded', 'deliverable', file.id, { name: file.name, version: String(file.version) })
  commit()
  const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString()
  const signature = Math.random().toString(36).slice(2, 14)
  return { url: `https://files.cavalladata.com/d/${file.id}?expires=${Date.parse(expiresAt) / 1000}&sig=${signature}`, expiresAt }
}

export async function deleteDeliverable(orgId: string, projectId: string, deliverableId: string): Promise<void> {
  if (LIVE_API) {
    await http('DELETE', `/orgs/${orgId}/projects/${projectId}/deliverables/${deliverableId}`)
    commit()
    return
  }
  await latency()
  const { userId } = projectScope(orgId, projectId, 'deliverables.delete')
  const file = db.deliverables.find((d) => d.id === deliverableId && d.projectId === projectId)
  if (!file) throw new PortalApiError('NOT_FOUND')
  const now = new Date().toISOString()
  // Soft delete the whole version chain.
  db.deliverables.filter((d) => (d.parentId ?? d.id) === (file.parentId ?? file.id)).forEach((d) => (d.deletedAt = now))
  audit(orgId, userId, 'deliverable.deleted', 'deliverable', file.id, { name: file.name })
  commit()
}

// ─── Messaging ────────────────────────────────────────────────────────────

export async function listConversations(orgId: string): Promise<ConversationSummary[]> {
  if (LIVE_API) return (await http<Paged<ConversationSummary>>('GET', `/orgs/${orgId}/conversations?limit=100`)).data
  await latency()
  const { userId, role } = scope(orgId, 'messages.view')
  const visible = new Set(visibleProjects(orgId, userId, role).map((p) => p.id))
  return db.conversations
    .filter((c) => c.orgId === orgId && (!c.projectId || visible.has(c.projectId)))
    .map((c) => {
      const messages = db.messages.filter((m) => m.conversationId === c.id)
      const lastRead = db.messageReads.find((r) => r.userId === userId && r.conversationId === c.id)?.lastReadAt ?? ''
      return {
        ...c,
        unreadCount: messages.filter((m) => m.senderId !== userId && m.createdAt > lastRead).length,
        lastMessage: messages[messages.length - 1] ?? null,
      }
    })
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export async function createConversation(orgId: string, input: { subject: string; projectId: string | null; body: string }): Promise<string> {
  const errors: Record<string, string> = {}
  if (!input.subject.trim()) errors.subject = 'Please add a subject.'
  if (!input.body.trim()) errors.body = 'Please write a message.'
  if (Object.keys(errors).length) throw new PortalApiError('VALIDATION_ERROR', undefined, errors)
  if (LIVE_API) {
    const conversation = await http<{ id: string }>('POST', `/orgs/${orgId}/conversations`, input)
    await sendMessage(orgId, conversation.id, input.body)
    return conversation.id
  }
  await latency()
  const { userId, role } = scope(orgId, 'messages.send')
  if (input.projectId && !visibleProjects(orgId, userId, role).some((p) => p.id === input.projectId)) throw new PortalApiError('NOT_FOUND')
  const now = new Date().toISOString()
  const id = newId('c')
  db.conversations.push({ id, orgId, projectId: input.projectId, subject: input.subject.trim(), updatedAt: now })
  db.messages.push({ id: newId('m'), conversationId: id, senderId: userId, body: input.body.trim(), createdAt: now, attachments: [] })
  db.messageReads.push({ userId, conversationId: id, lastReadAt: now })
  commit()
  return id
}

export async function listMessages(orgId: string, conversationId: string): Promise<Paged<Message & { sender: PortalUser }>> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/conversations/${conversationId}/messages?limit=100`)
  await latency(160)
  const { userId, role } = scope(orgId, 'messages.view')
  const conversation = db.conversations.find((c) => c.id === conversationId && c.orgId === orgId)
  const visible = new Set(visibleProjects(orgId, userId, role).map((p) => p.id))
  if (!conversation || (conversation.projectId && !visible.has(conversation.projectId))) throw new PortalApiError('NOT_FOUND')
  const rows = db.messages
    .filter((m) => m.conversationId === conversationId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((m) => ({ ...m, sender: userById(m.senderId) }))
  return paginate(rows, { limit: 100 })
}

export async function sendMessage(orgId: string, conversationId: string, body: string): Promise<void> {
  if (!body.trim()) throw new PortalApiError('VALIDATION_ERROR', 'Please write a message.', { body: 'Required.' })
  if (body.length > 5000) throw new PortalApiError('VALIDATION_ERROR', 'Messages can be up to 5,000 characters.', { body: 'Too long.' })
  if (LIVE_API) {
    await http('POST', `/orgs/${orgId}/conversations/${conversationId}/messages`, { body })
    commit()
    return
  }
  await latency(180)
  const { userId } = scope(orgId, 'messages.send')
  const conversation = db.conversations.find((c) => c.id === conversationId && c.orgId === orgId)
  if (!conversation) throw new PortalApiError('NOT_FOUND')
  const now = new Date().toISOString()
  db.messages.push({ id: newId('m'), conversationId, senderId: userId, body: body.trim(), createdAt: now, attachments: [] })
  conversation.updatedAt = now
  const read = db.messageReads.find((r) => r.userId === userId && r.conversationId === conversationId)
  if (read) read.lastReadAt = now
  else db.messageReads.push({ userId, conversationId, lastReadAt: now })
  commit()
}

export async function markConversationRead(orgId: string, conversationId: string): Promise<void> {
  if (LIVE_API) {
    await http('POST', `/orgs/${orgId}/conversations/${conversationId}/read`)
    commit()
    return
  }
  const { userId } = scope(orgId, 'messages.view')
  const now = new Date().toISOString()
  const read = db.messageReads.find((r) => r.userId === userId && r.conversationId === conversationId)
  if (read && read.lastReadAt >= now) return
  if (read) read.lastReadAt = now
  else db.messageReads.push({ userId, conversationId, lastReadAt: now })
  commit()
}

// ─── Billing ──────────────────────────────────────────────────────────────

function visibleInvoices(orgId: string, userId: string, role: OrgRole): Invoice[] {
  const inOrg = db.invoices.filter((i) => i.orgId === orgId && i.status !== 'draft')
  if (can(role, 'invoices.viewAll')) return inOrg
  if (can(role, 'invoices.viewOwn')) return inOrg.filter((i) => i.billedTo === userId)
  throw new PortalApiError('FORBIDDEN')
}

export async function listInvoices(orgId: string, query: PageQuery & { status?: InvoiceStatus } = {}): Promise<Paged<Invoice>> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/invoices${queryString({ ...query })}`)
  await latency()
  const { userId, role } = scope(orgId)
  const rows = visibleInvoices(orgId, userId, role)
    .filter((i) => !query.status || i.status === query.status)
    .sort((a, b) => (b.sentAt ?? '').localeCompare(a.sentAt ?? ''))
  return paginate(rows, query)
}

export async function getInvoice(orgId: string, invoiceId: string): Promise<Invoice> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/invoices/${invoiceId}`)
  await latency(160)
  const { userId, role } = scope(orgId)
  const invoice = visibleInvoices(orgId, userId, role).find((i) => i.id === invoiceId)
  if (!invoice) throw new PortalApiError('NOT_FOUND')
  return JSON.parse(JSON.stringify(invoice)) as Invoice
}

/** Starts a Stripe payment. Live mode returns a Stripe Checkout URL to redirect to. */
export async function payInvoice(orgId: string, invoiceId: string): Promise<{ checkoutUrl: string | null }> {
  if (LIVE_API) return http('POST', `/orgs/${orgId}/invoices/${invoiceId}/pay`)
  await latency(900)
  const { userId } = scope(orgId, 'invoices.pay')
  const invoice = db.invoices.find((i) => i.id === invoiceId && i.orgId === orgId)
  if (!invoice) throw new PortalApiError('NOT_FOUND')
  if (invoice.status !== 'sent' && invoice.status !== 'overdue') throw new PortalApiError('VALIDATION_ERROR', 'This invoice is not open for payment.')
  const now = new Date().toISOString()
  invoice.status = 'paid'
  invoice.paidAt = now
  db.payments.unshift({ id: newId('pay'), invoiceId, amountCents: invoice.totalCents, currency: invoice.currency, method: 'Card (preview)', status: 'succeeded', paidAt: now })
  audit(orgId, userId, 'invoice.paid', 'invoice', invoiceId, { number: invoice.invoiceNumber })
  commit()
  return { checkoutUrl: null }
}

export async function invoicePdf(orgId: string, invoiceId: string): Promise<{ url: string }> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/invoices/${invoiceId}/pdf`)
  await latency(300)
  const { userId, role } = scope(orgId)
  const invoice = visibleInvoices(orgId, userId, role).find((i) => i.id === invoiceId)
  if (!invoice) throw new PortalApiError('NOT_FOUND')
  return { url: `https://files.cavalladata.com/invoices/${invoice.invoiceNumber}.pdf?expires=${Math.floor(Date.now() / 1000) + 300}&sig=${Math.random().toString(36).slice(2, 12)}` }
}

export async function listPayments(orgId: string): Promise<Paged<Payment>> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/payments`)
  await latency()
  scope(orgId, 'payments.view')
  const invoiceIds = new Set(db.invoices.filter((i) => i.orgId === orgId).map((i) => i.id))
  return paginate(db.payments.filter((p) => invoiceIds.has(p.invoiceId)))
}

// ─── Audit and notifications ──────────────────────────────────────────────

export async function listAudit(orgId: string, query: PageQuery = {}): Promise<Paged<AuditEntry & { user: PortalUser | null }>> {
  if (LIVE_API) return http('GET', `/orgs/${orgId}/audit${queryString({ ...query })}`)
  await latency()
  scope(orgId, 'audit.view')
  const rows = db.auditLog
    .filter((a) => a.orgId === orgId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map((a) => ({ ...a, user: a.userId ? userById(a.userId) : null }))
  return paginate(rows, { limit: 20, ...query })
}

export async function listNotifications(): Promise<Paged<PortalNotification>> {
  if (LIVE_API) return http('GET', '/notifications?limit=20')
  await latency(120)
  const { user } = requireSession()
  return paginate(db.notifications.filter((n) => n.userId === user.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), { limit: 20 })
}

export async function markNotificationRead(id: string): Promise<void> {
  if (LIVE_API) {
    await http('PATCH', `/notifications/${id}/read`)
    commit()
    return
  }
  const { user } = requireSession()
  const note = db.notifications.find((n) => n.id === id && n.userId === user.id)
  if (note && !note.isRead) {
    note.isRead = true
    commit()
  }
}

export async function markAllNotificationsRead(): Promise<void> {
  if (LIVE_API) {
    await http('POST', '/notifications/read-all')
    commit()
    return
  }
  const { user } = requireSession()
  db.notifications.filter((n) => n.userId === user.id).forEach((n) => (n.isRead = true))
  commit()
}

// ─── Helpers ──────────────────────────────────────────────────────────────

export function productName(productId: string): string {
  return db.products.find((p) => p.id === productId)?.name ?? 'Cavalla'
}

function putWithProgress(url: string, file: File, onProgress?: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.upload.onprogress = (event) => event.lengthComputable && onProgress?.(Math.round((event.loaded / event.total) * 100))
    xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new PortalApiError('INTERNAL_ERROR', 'The upload did not complete. Please try again.')))
    xhr.onerror = () => reject(new PortalApiError('INTERNAL_ERROR', 'The upload did not complete. Check your connection and try again.'))
    xhr.send(file)
  })
}
